import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, Search, Loader2, ToggleLeft, ToggleRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AdminTechniquesPage = () => {
    const navigate = useNavigate();
    const [techniques, setTechniques] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const getAuthHeaders = () => {
        const token = localStorage.getItem('admin_token');
        return { Authorization: `Bearer ${token}` };
    };

    const fetchTechniques = async () => {
        try {
            const response = await axios.get(
                `${API_URL}/api/admin/techniques`,
                { headers: getAuthHeaders() }
            );
            setTechniques(response.data.techniques || []);
        } catch (error) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                toast.error('Session expired. Please login again.');
                navigate('/admin/login');
            } else {
                toast.error('Failed to fetch techniques');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTechniques();
    }, []);

    const handleDelete = async (slug, title) => {
        if (!window.confirm(`Are you sure you want to delete "${title}"? This cannot be undone.`)) return;
        try {
            await axios.delete(
                `${API_URL}/api/admin/techniques/${slug}`,
                { headers: getAuthHeaders() }
            );
            toast.success(`"${title}" deleted`);
            fetchTechniques();
        } catch (error) {
            toast.error('Failed to delete technique');
        }
    };

    const handleToggleStatus = async (slug, currentStatus) => {
        try {
            const response = await axios.patch(
                `${API_URL}/api/admin/techniques/${slug}/toggle-status`,
                {},
                { headers: getAuthHeaders() }
            );
            if (response.data.success) {
                toast.success(`Status changed to ${response.data.status}`);
                fetchTechniques();
            }
        } catch (error) {
            toast.error('Failed to toggle status');
        }
    };

    const handleBulkPublish = async () => {
        if (!window.confirm(`Publish all ${draftCount} draft techniques?`)) return;
        try {
            const response = await axios.post(
                `${API_URL}/api/admin/techniques/bulk-publish`,
                {},
                { headers: getAuthHeaders() }
            );
            if (response.data.success) {
                toast.success(`Published ${response.data.published_count} techniques`);
                fetchTechniques();
            }
        } catch (error) {
            toast.error('Failed to bulk publish');
        }
    };

    const filteredTechniques = techniques.filter(t => {
        const matchesSearch =
            t.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.category?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const publishedCount = techniques.filter(t => t.status === 'published').length;
    const draftCount = techniques.filter(t => t.status === 'draft').length;

    const getDifficultyColor = (difficulty) => {
        switch (difficulty?.toLowerCase()) {
            case 'beginner': return 'bg-green-100 text-green-800';
            case 'intermediate': return 'bg-yellow-100 text-yellow-800';
            case 'advanced': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="techniques-loading">
                <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4" data-testid="admin-techniques-page">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold" data-testid="techniques-title">Techniques Manager</h1>
                        <p className="text-gray-500 text-sm">
                            {techniques.length} total &middot; {publishedCount} published &middot; {draftCount} drafts
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Link to="/admin/recipes">
                            <Button variant="outline" data-testid="back-to-recipes-btn">Recipes</Button>
                        </Link>
                        <Link to="/admin/techniques/import">
                            <Button variant="outline" data-testid="import-techniques-btn">Import JSON</Button>
                        </Link>
                        {draftCount > 0 && (
                            <Button
                                variant="outline"
                                className="border-green-500 text-green-700 hover:bg-green-50"
                                onClick={handleBulkPublish}
                                data-testid="bulk-publish-btn"
                            >
                                <CheckCircle2 className="w-4 h-4 mr-2" /> Publish All Drafts ({draftCount})
                            </Button>
                        )}
                        <Link to="/admin/techniques/new">
                            <Button className="bg-amber-700 hover:bg-amber-800" data-testid="new-technique-btn">
                                <Plus className="w-4 h-4 mr-2" /> New Technique
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex gap-3 mb-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by title or category..."
                            className="pl-10"
                            data-testid="technique-search-input"
                        />
                    </div>
                    <div className="flex gap-1 bg-white border rounded-md p-1">
                        {['all', 'published', 'draft'].map(s => (
                            <Button
                                key={s}
                                variant={statusFilter === s ? 'default' : 'ghost'}
                                size="sm"
                                className={statusFilter === s ? 'bg-amber-700 hover:bg-amber-800 text-white' : ''}
                                onClick={() => setStatusFilter(s)}
                                data-testid={`filter-${s}-btn`}
                            >
                                {s === 'all' ? `All (${techniques.length})` :
                                 s === 'published' ? `Published (${publishedCount})` :
                                 `Draft (${draftCount})`}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Techniques List */}
                {filteredTechniques.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <p className="text-gray-500" data-testid="no-techniques-msg">
                                {searchQuery || statusFilter !== 'all'
                                    ? 'No techniques match your filters'
                                    : 'No techniques yet. Create your first one!'}
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid gap-3" data-testid="techniques-list">
                        {filteredTechniques.map((technique) => (
                            <Card key={technique.slug} className="hover:shadow-md transition-shadow" data-testid={`technique-card-${technique.slug}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start gap-4">
                                        {/* Image thumbnail */}
                                        <div className="w-20 h-20 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                                            {technique.image_url ? (
                                                <img
                                                    src={technique.image_url.startsWith('/') ? `${API_URL}${technique.image_url}` : technique.image_url}
                                                    alt={technique.title}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                                    No img
                                                </div>
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <h3 className="font-semibold text-lg truncate">{technique.title}</h3>
                                                    <div className="flex flex-wrap gap-2 mt-1">
                                                        <Badge
                                                            className={technique.status === 'published'
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-yellow-100 text-yellow-800'}
                                                        >
                                                            {technique.status || 'draft'}
                                                        </Badge>
                                                        {technique.category && (
                                                            <Badge variant="outline">{technique.category}</Badge>
                                                        )}
                                                        {technique.difficulty && (
                                                            <Badge className={getDifficultyColor(technique.difficulty)}>
                                                                {technique.difficulty}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-1 flex-shrink-0">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleToggleStatus(technique.slug, technique.status)}
                                                        title={technique.status === 'published' ? 'Unpublish' : 'Publish'}
                                                        data-testid={`toggle-status-${technique.slug}`}
                                                    >
                                                        {technique.status === 'published'
                                                            ? <ToggleRight className="w-5 h-5 text-green-600" />
                                                            : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                                                    </Button>
                                                    <Link to={`/en/techniques/${technique.slug}`} target="_blank">
                                                        <Button variant="ghost" size="sm" data-testid={`view-${technique.slug}`}>
                                                            <Eye className="w-4 h-4" />
                                                        </Button>
                                                    </Link>
                                                    <Link to={`/admin/techniques/${technique.slug}/edit`}>
                                                        <Button variant="ghost" size="sm" data-testid={`edit-${technique.slug}`}>
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                    </Link>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => handleDelete(technique.slug, technique.title)}
                                                        data-testid={`delete-${technique.slug}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            {technique.introduction && (
                                                <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                                                    {technique.introduction}
                                                </p>
                                            )}
                                            <p className="text-xs text-gray-400 mt-1">
                                                Slug: {technique.slug}
                                                {technique.readTime && ` · ${technique.readTime}`}
                                                {technique.sections && ` · ${technique.sections.length} section${technique.sections.length !== 1 ? 's' : ''}`}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminTechniquesPage;
